import { getQueryParameters } from '@sixfold/app-utils';
import { SpinnerOverlay } from '@sixfold/common-ui';
import { NestedError, isNil } from '@sixfold/typed-primitives';
import * as React from 'react';
import { useLocation } from 'react-router';

import { BackendError } from '../../../shared/types/error';
import { ErrorView } from '../../components/error_view';
import { SharedView, SharedViewPicker } from '../../components/shared_view_picker';
import { buildUrl } from '../../lib/build_url';
import * as Routes from '../../lib/routes';
import { navigateToLocationWithServerSideRendering, fetchJSON } from '../../lib/util';

export const SharedViewSwitcherContainer: React.FC = () => {
  const next = getQueryParameters(useLocation().search).next;
  const { sharedViews, isLoading } = useUserSharedViews();

  if (isLoading) {
    return <SpinnerOverlay />;
  }

  if (isNil(sharedViews)) {
    return <ErrorView error={{ type: BackendError.SHARED_VIEW }} />;
  }

  return (
    <SharedViewPicker
      sharedViews={sharedViews}
      onNavigate={(sharedViewId?: string) => {
        navigateToLocationWithServerSideRendering(
          buildUrl(Routes.SharedViewNavigate.generatePath({}), [
            { key: 'shared_view_id', value: sharedViewId },
            { key: 'next', value: next },
          ]),
        );
      }}
    />
  );
};

const useUserSharedViews = () => {
  const [sharedViews, setSharedViews] = React.useState<SharedView[] | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchSharedViews = async () => {
      try {
        const sharedViewData = await fetchJSON<SharedView[]>(Routes.SharedViews.routerPath);
        setSharedViews(sharedViewData);
      } catch (err) {
        console.error(new NestedError('Failed to fetch shared view data', err));
      } finally {
        setIsLoading(false);
      }
    };

    void fetchSharedViews();
  }, []);

  return { sharedViews, isLoading };
};
